import { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import { FormLayout } from 'components/form/formLayout/formLayout';
import { FormFieldDescription } from 'components/form/formLayout/types';
import { Input, InputType } from 'components/inputs';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { SelectItem } from 'components/inputs/select/select';

import { CUSTOMER_BASE_FIELDS, FORM_FIELDS, FormFieldType, LABELS } from '../keys';
import { FormContainer, SelectWrapper } from '../styles';

interface CustomerDetailsFormProps {
  residentialCountries: SelectItem[];
  readOnlyMode: boolean;
  values: Record<FormFieldType, string | boolean>;
  errors?: FormikErrors<Record<FormFieldType, any>>;
  setFieldValue: (field: FormFieldType, value: any) => void;
}

const CustomerDetailsForm = ({ residentialCountries, values, readOnlyMode, errors, setFieldValue }: CustomerDetailsFormProps) => {
  const { t } = useTranslation();

  const onCountryChange = (item: SelectItem) => {
    setFieldValue(FORM_FIELDS.COUNTRY_OF_RESIDENCE, item.key);
  };

  const getCountrySelectItem = (countryKey: string) => residentialCountries.find((country) => country.key === countryKey);

  const fields: FormFieldDescription[] = CUSTOMER_BASE_FIELDS.map((field) => {
    if (field === FORM_FIELDS.COUNTRY_OF_RESIDENCE) {
      return {
        key: field,
        label: t(LABELS.FORM_FIELDS.LABELS[field]),
        renderInput: ({ readonly }) => (
          <SelectWrapper>
            <SearchableSelect<SelectItem, false>
              fullWidth
              closeDropdownOnSelect
              getItemLabel={({ label }) => label}
              placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS[field])}
              items={residentialCountries}
              disabled={readonly}
              multiple={false}
              value={values?.[field] ? getCountrySelectItem(values[field] as string) : undefined}
              onChange={onCountryChange}
            />
          </SelectWrapper>
        ),
      };
    }

    if (field === FORM_FIELDS.DOB) {
      return {
        key: field,
        label: t(LABELS.FORM_FIELDS.LABELS[field]),
        renderInput: ({ readonly }) => (
          <Input
            type={InputType.Date}
            fullWidth
            value={values?.[field] as string}
            onChange={(value) => setFieldValue(field, value)}
            disabled={readonly}
            placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS[field])}
          />
        ),
      };
    }

    return {
      key: field,
      label: t(LABELS.FORM_FIELDS.LABELS[field]),
      renderInput: ({ readonly }) => (
        <Input
          fullWidth
          value={values?.[field] as string}
          onChange={(value) => setFieldValue(field, value)}
          disabled={readonly}
          placeholder={t(LABELS.FORM_FIELDS.PLACEHOLDERS[field])}
        />
      ),
    };
  });

  return (
    <Panel label={t(LABELS.CUSTOMER_DETAILS_PANEL_TITLE)}>
      <FormContainer>
        <FormLayout fields={fields} readonly={readOnlyMode} error={errors as Record<string, string>} />
      </FormContainer>
    </Panel>
  );
};

export default CustomerDetailsForm;
